import { z } from 'zod';

import { AnalyticalVariableCriterionSchema, AnalyticalVariableSchema } from './AnalyticalVariable';
import {
  Attribute,
  CO_UF_DateAttributesEnumLegacy,
  ConceptAttributesConditionOccurrenceEnumLegacy,
  ConceptAttributesDrugExposureEnumLegacy,
  ConceptAttributesEnumLegacy,
  ConceptAttributesMeasurementEnumLegacy,
  ConceptAttributesObservationEnumLegacy,
  ConceptAttributesProcedureOccurrenceEnumLegacy,
  ConceptAttributesVisitOccurrenceEnumLegacy,
  DE_UF_DateAttributesEnumLegacy,
  DerivedEventType,
  DerivedEventTypeLegacy,
  IndexDateTypeLegacy,
  ME_UF_DateAttributesEnumLegacy,
  NumericFiltersAttributesDrugExposureEnumLegacy,
  NumericFiltersAttributesEnumLegacy,
  NumericFiltersAttributesMeasurementEnumLegacy,
  NumericFiltersAttributesObservationEnumLegacy,
  NumericFiltersAttributesProcedureOccurrenceEnumLegacy,
  OB_UF_DateAttributesEnumLegacy,
  PO_UF_DateAttributesEnumLegacy,
  RE_EntityFilterEnumLegacy,
  SimpleEventType,
  SimpleEventTypeLegacy,
  SubjectVariableCriteriaSelectionTypeLegacy,
  SubjectVariableTypeLegacy,
  TemporalConstraintsEnumLegacy,
  TimeWindowDateTypeEnum,
  TimeWindowRelativeDateDirectionEnum,
  UniquenessFilterTimeUnitEnumLegacy,
  UniquenessFilterTypesEnumLegacy,
  VO_UF_DateAttributesEnumLegacy,
} from '../lib/cohortBuilderLegacy';

// TODO: This is duplicate of /app-state-cohort-builder/src/lib/constants/constants.ts, remove from constants.ts
const NAME_MAX_CHARACTER_LENGTH = 214;
const NAME_VALID_CHARACTERS_WITHOUT_DOUBLE_QUOTE =
  /^[a-z0-9><=≥≤+\-_– ~`!@#$%^&*(){}[\]|/;'’,.?ö]*$/i;

// Base enums
const DerivedEventTypeSchema = z.enum([
  ...Object.values(DerivedEventTypeLegacy),
  ...Object.values(DerivedEventType),
]);
const UniquenessFilterTypeSchema = z.enum(Object.values(UniquenessFilterTypesEnumLegacy));
const TimeUnitSchema = z.enum(Object.values(UniquenessFilterTimeUnitEnumLegacy));
const TemporalConstraintTypeSchema = z.enum(Object.values(TemporalConstraintsEnumLegacy));

// New time window enums
const TimeWindowDateTypeSchema = z.enum(Object.values(TimeWindowDateTypeEnum));
const TimeWindowRelativeDateDirectionSchema = z.enum(
  Object.values(TimeWindowRelativeDateDirectionEnum)
);

const SimpleEventTypeSchema = z.enum([
  ...Object.values(SimpleEventTypeLegacy),
  ...Object.values(SimpleEventType),
]);

const RelatedClinicalTableSchema = z.enum(Object.values(RE_EntityFilterEnumLegacy));

const DemographicConceptAttributeSchema = z.enum(Object.values(ConceptAttributesEnumLegacy));

const ConditionConceptAttributeSchema = z.enum(
  Object.values(ConceptAttributesConditionOccurrenceEnumLegacy)
);

const DrugExposureConceptAttributeSchema = z.enum(
  Object.values(ConceptAttributesDrugExposureEnumLegacy)
);

const MeasurementConceptAttributeSchema = z.enum(
  Object.values(ConceptAttributesMeasurementEnumLegacy)
);

const ObservationConceptAttributeSchema = z.enum(
  Object.values(ConceptAttributesObservationEnumLegacy)
);

const ProcedureConceptAttributeSchema = z.enum(
  Object.values(ConceptAttributesProcedureOccurrenceEnumLegacy)
);

const DrugExposureNumericAttributesSchema = z.enum(
  Object.values(NumericFiltersAttributesDrugExposureEnumLegacy)
);

const MeasurementNumericAttributesSchema = z.enum(
  Object.values(NumericFiltersAttributesMeasurementEnumLegacy)
);

const ObservationNumericAttributesSchema = z.enum(
  Object.values(NumericFiltersAttributesObservationEnumLegacy)
);

const ProcedureNumericAttributesSchema = z.enum(
  Object.values(NumericFiltersAttributesProcedureOccurrenceEnumLegacy)
);

const ProviderConceptAttributeSchema = z.enum([Attribute.SPECIALTY_CONCEPT]);

const DemographicNumericAttributesSchema = z.enum(
  Object.values(NumericFiltersAttributesEnumLegacy)
);

const DateAttributesSchema = z.enum([
  ...Object.values(CO_UF_DateAttributesEnumLegacy),
  ...Object.values(DE_UF_DateAttributesEnumLegacy),
  ...Object.values(VO_UF_DateAttributesEnumLegacy),
  ...Object.values(ME_UF_DateAttributesEnumLegacy),
  ...Object.values(OB_UF_DateAttributesEnumLegacy),
  ...Object.values(PO_UF_DateAttributesEnumLegacy),
  Attribute.DERIVED_EVENT_DATE,
]);

const VisitOccurrenceConceptAttributeSchema = z.enum(
  Object.values(ConceptAttributesVisitOccurrenceEnumLegacy)
);

// Field schemas
const ConceptOperatorFieldSchema = z.object({
  value: z.literal('IN'),
});

const EventNameFieldSchema = z.object({
  value: z.string().describe("Event name for filtering. Example: DRUG_EXPOSURE::'Aspirin'"),
});

const ConditionAttributeFieldSchema = z.object({
  value: ConditionConceptAttributeSchema.describe('Condition concept attribute for filtering.'),
});

const DemographicsAttributeFieldSchema = z.object({
  value: DemographicConceptAttributeSchema.describe('Demographic concept attribute for filtering.'),
});

const DemographicsNumericFieldSchema = z.object({
  value: DemographicNumericAttributesSchema.describe(
    'Demographic numeric attribute for filtering.'
  ),
});

const DrugExposureAttributeFieldSchema = z.object({
  value: DrugExposureConceptAttributeSchema.describe(
    'Drug exposure concept attribute for filtering.'
  ),
});

const MeasurementAttributeFieldSchema = z.object({
  value: MeasurementConceptAttributeSchema.describe('Measurement concept attribute for filtering.'),
});

const ObservationAttributeFieldSchema = z.object({
  value: ObservationConceptAttributeSchema.describe('Observation concept attribute for filtering.'),
});

const ProcedureAttributeFieldSchema = z.object({
  value: ProcedureConceptAttributeSchema.describe('Procedure concept attribute for filtering.'),
});

const ProviderAttributeFieldSchema = z.object({
  value: ProviderConceptAttributeSchema.describe('Provider concept attribute for filtering.'),
});

const VisitOccurrenceAttributeFieldSchema = z.object({
  value: VisitOccurrenceConceptAttributeSchema.describe(
    'Visit occurrence concept attribute for filtering.'
  ),
});

const ConceptGroupFieldSchema = z.object({
  value: z
    .string()
    .describe(
      'Concept group for filtering. Should be in the format `CONCEPT_GROUP::"value"`. For more than one concept, use the format `CONCEPT_GROUP::"value1",CONCEPT_GROUP::"value2",CONCEPT_GROUP::"value3"`'
    ),
});

const NumericAttributeFieldSchema = z.object({
  value: z
    .union([
      DrugExposureNumericAttributesSchema,
      MeasurementNumericAttributesSchema,
      ObservationNumericAttributesSchema,
      ProcedureNumericAttributesSchema,
      DemographicNumericAttributesSchema,
    ])
    .describe(
      'Numeric filtering for Demographic, Drug Exposure, Measurement, Observation, and Procedure events.'
    ),
});

const InFieldSchema = z.object({
  value: z.literal('IN').describe('IN operator for filtering. Example: IN'),
});

const OperatorFieldSchema = z.object({
  value: z.string().describe('Operator for numeric filtering. Example: >, >=, <, <=, =, !='),
});

const ValueFieldSchema = z.object({
  value: z.string().describe('Value for filtering. Example: 1, 2, 3.5'),
});

const RelatedClinicalTableFieldSchema = z.object({
  value: RelatedClinicalTableSchema.describe('Related clinical table for filtering.'),
});

const UniquenessFilterInputFieldSchema = z.object({
  value: z
    .union([UniquenessFilterTypeSchema, TimeUnitSchema, z.string(), DateAttributesSchema])
    .describe('Input field for uniqueness filter.'),
});

const EventDateAttributeFieldSchema = z.object({
  value: DateAttributesSchema.describe('Event date attribute for filtering.'),
});

const PositionFieldSchema = z.object({
  value: z.enum(['MIN', 'MAX', 'INDEX']).describe('Position for filtering.'),
});

const IsSelectedFieldSchema = z.object({
  value: z.enum(['true', 'false']).describe('Selection status for filtering.'),
});

const TemporalConstraintInputFieldSchema = z.object({
  value: z
    .union([TemporalConstraintTypeSchema, z.string(), TimeUnitSchema])
    .describe('Input field for temporal constraint.'),
});

const TimeWindowDateFieldSchema = z.object({
  value: z
    .string()
    .describe('Absolute date value for the time window date field, in YYYY-MM-DD format.'),
});

// Filter schemas
const DemographicsConceptFilterSchema = z.object({
  inputFields: z
    .array(
      z.union([
        DemographicsAttributeFieldSchema,
        ConceptOperatorFieldSchema,
        ConceptGroupFieldSchema,
      ])
    )
    .length(3)
    .describe(
      'List of exactly 3 input fields for the demographics concept filter: [0] demographics attribute (GENDER_CONCEPT, RACE_CONCEPT, ETHNICITY_CONCEPT), [1] concept operator (IN), [2] concept group (CONCEPT_GROUP::"value")'
    ),
});

const DemographicsNumericFilterSchema = z.object({
  inputFields: z
    .array(z.union([DemographicsNumericFieldSchema, OperatorFieldSchema, ValueFieldSchema]))
    .describe('Input fields for demographics numeric filter.'),
});

const ConditionConceptFilterSchema = z.object({
  inputFields: z
    .array(
      z.union([ConditionAttributeFieldSchema, ConceptOperatorFieldSchema, ConceptGroupFieldSchema])
    )
    .length(3)
    .describe(
      'List of exactly 3 input fields for the condition concept filter: [0] condition attribute (CONDITION_CONCEPT, CONDITION_TYPE_CONCEPT, CONDITION_STATUS_CONCEPT, CONDITION_SOURCE_CONCEPT), [1] concept operator (IN), [2] concept group (CONCEPT_GROUP::"value")'
    ),
});

const DrugExposureConceptFilterSchema = z.object({
  inputFields: z
    .array(
      z.union([
        DrugExposureAttributeFieldSchema,
        ConceptOperatorFieldSchema,
        ConceptGroupFieldSchema,
      ])
    )
    .length(3)
    .describe(
      'List of exactly 3 input fields for the drug exposure concept filter: [0] drug exposure attribute (DRUG_CONCEPT, DRUG_TYPE_CONCEPT, DRUG_ROUTE_CONCEPT, DRUG_FORM_CONCEPT, DRUG_SOURCE_CONCEPT), [1] concept operator (IN), [2] concept group (CONCEPT_GROUP::"value")'
    ),
});

const MeasurementConceptFilterSchema = z.object({
  inputFields: z
    .array(
      z.union([
        MeasurementAttributeFieldSchema,
        ConceptOperatorFieldSchema,
        ConceptGroupFieldSchema,
      ])
    )
    .describe('Input fields for measurement concept filter.'),
});

const ObservationConceptFilterSchema = z.object({
  inputFields: z
    .array(
      z.union([
        ObservationAttributeFieldSchema,
        ConceptOperatorFieldSchema,
        ConceptGroupFieldSchema,
      ])
    )
    .describe('Input fields for observation concept filter.'),
});

const ProcedureConceptFilterSchema = z.object({
  inputFields: z
    .array(
      z.union([ProcedureAttributeFieldSchema, ConceptOperatorFieldSchema, ConceptGroupFieldSchema])
    )
    .describe('Input fields for procedure concept filter.'),
});

const NumericFilterSchema = z.object({
  inputFields: z
    .array(z.union([NumericAttributeFieldSchema, OperatorFieldSchema, ValueFieldSchema]))
    .describe('Input fields for numeric filter.'),
});

const RelatedEntityFilterSchema = z.object({
  inputFields: z
    .array(
      z.union([
        RelatedClinicalTableFieldSchema,
        ConditionAttributeFieldSchema,
        ProviderAttributeFieldSchema,
        VisitOccurrenceAttributeFieldSchema,
        InFieldSchema,
        ConceptGroupFieldSchema,
      ])
    )
    .describe(
      'List of exactly 4 input fields for the related entity filter: [0] related clinical table (CONDITION_OCCURRENCE, PROVIDER, VISIT_OCCURRENCE), [1] condition attribute (CONDITION_CONCEPT, CONDITION_TYPE_CONCEPT, CONDITION_STATUS_CONCEPT, CONDITION_SOURCE_CONCEPT), [2] operator (IN), [3] concept group (CONCEPT_GROUP::"value")'
    ),
});

const UniquenessFilterSchema = z.object({
  inputFields: z
    .array(UniquenessFilterInputFieldSchema)
    .describe('Input fields for uniqueness filter.'),
});

const EventFilterSchema = z.object({
  eventName: EventNameFieldSchema,
  eventDateAttribute: EventDateAttributeFieldSchema,
  position: PositionFieldSchema.optional().nullable(),
  isSelected: IsSelectedFieldSchema.optional().nullable(),
  index: ValueFieldSchema.optional().nullable(),
});

const TemporalConstraintFilterSchema = z.object({
  inputFields: z
    .array(TemporalConstraintInputFieldSchema)
    .describe('Input fields for temporal constraint filter.'),
});

const CountFilterSchema = z.object({
  inputFields: z
    .array(z.union([EventNameFieldSchema, OperatorFieldSchema, ValueFieldSchema]))
    .describe('Input fields for count filter.'),
});

const TimeWindowRelativeDateSchema = z.object({
  value: z.number().int().min(0).describe('Relative number of days before or after the index date'),
  timeUnit: TimeUnitSchema.describe('Relative time unit'),
  direction: TimeWindowRelativeDateDirectionSchema.describe(
    'Whether the relative date is before or after the index date'
  ),
});

const TimeWindowDateFilterSchema = z.object({
  type: TimeWindowDateTypeSchema.describe(
    'Type of the time window date. It can be either relative to index date or absolute date or from the start of the data'
  ),
  date: TimeWindowDateFieldSchema.nullish().describe(
    'Absolute date value for the time window date field, in YYYY-MM-DD format. Should only be used if type is ABSOLUTE_DATE'
  ),
  relativeDate: TimeWindowRelativeDateSchema.nullish().describe(
    'Relative date value for the time window date field. Should only be used if type is RELATIVE_TO_INDEX_DATE'
  ),
});

const TimeWindowFilterSchema = z.object({
  startDate: TimeWindowDateFilterSchema.nullish().describe('Start date of the time window'),
  endDate: TimeWindowDateFilterSchema.nullish().describe('End date of the time window'),
});

// Component schemas
const CriteriaSelectionSchema = z.enum(Object.values(SubjectVariableCriteriaSelectionTypeLegacy));
const SubjectVariableTypeSchema = z.enum(Object.values(SubjectVariableTypeLegacy));

// Configuration schemas
const IndexDateTypeSchema = z.enum(Object.values(IndexDateTypeLegacy));

const IndexDateRangeSchema = z.object({
  startDate: z.string().describe('Start date of the index date range, in YYYY-MM-DD format'),
  endDate: z.string().describe('End date of the index date range, in YYYY-MM-DD format'),
});

const BaselinePeriodRestrictionSchema = z.object({
  isEnabled: z.boolean().describe('The baseline period restriction is enabled/disabled'),
  daysBeforeIndexDate: z
    .number()
    .int()
    .positive()
    .nullish()
    .describe(
      'Number of days before the index date to include, must be positive, inclusive, or None if disabled'
    ),
});

const FollowUpPeriodRestrictionSchema = z.object({
  isEnabled: z.boolean().describe('The follow-up period restriction is enabled/disabled'),
  daysAfterIndexDate: z
    .number()
    .int()
    .positive()
    .nullish()
    .describe(
      'Number of days after the index date to include, must be positive, inclusive, or None if disabled'
    ),
});

const OverallStudyPeriodRestrictionSchema = z.object({
  isEnabled: z.boolean().describe('The overall study period restriction is enabled/disabled'),
  startDate: z
    .string()
    .optional()
    .describe(
      'Start date of the overall study period restriction, in YYYY-MM-DD format, inclusive, or None if disabled'
    ),
  endDate: z
    .string()
    .optional()
    .describe(
      'End date of the overall study period restriction, in YYYY-MM-DD format, inclusive, or None if disabled'
    ),
});

const SimpleEventSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(NAME_MAX_CHARACTER_LENGTH)
    .regex(NAME_VALID_CHARACTERS_WITHOUT_DOUBLE_QUOTE)
    .describe(
      'Event name with format EVENT_TYPE::"value". Ex: DRUG_EXPOSURE::"Aspirin", CONDITION_OCCURRENCE::"Diabetes"'
    ),
  type: SimpleEventTypeSchema.describe('Type of simple event.'),
  conceptFilters: z
    .array(
      z.union([
        ConditionConceptFilterSchema,
        DrugExposureConceptFilterSchema,
        MeasurementConceptFilterSchema,
        ObservationConceptFilterSchema,
        ProcedureConceptFilterSchema,
      ])
    )
    .describe('Filter on concepts which depend on the type of event'),
  numericFilters: z
    .array(NumericFilterSchema)
    .describe('Filter on numeric values which depend on the type of event'),
  relatedEntityFilters: z
    .array(RelatedEntityFilterSchema)
    .describe('Filter on related clinical tables which depend on the type of event'),
  uniquenessFilters: z
    .array(UniquenessFilterSchema)
    .max(1)
    .describe('Uniqueness filters for the event'),
});

const DerivedEventSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(NAME_MAX_CHARACTER_LENGTH)
    .regex(NAME_VALID_CHARACTERS_WITHOUT_DOUBLE_QUOTE)
    .describe(
      'Event name with format EVENT_TYPE::"value". Ex: MERGED_EVENT::"asthma exacerbation"'
    ),
  type: DerivedEventTypeSchema.describe('Type of derived event'),
  anyOrder: z
    .boolean()
    .nullish()
    .describe('Whether the events can occur in any order, only to be used for sequence events'),
  temporalConstraints: z
    .array(TemporalConstraintFilterSchema)
    .describe('Temporal constraints for the derived event, only to be used for sequence events'),
  eventFilters: z
    .array(EventFilterSchema)
    .describe(
      'Filters for the events which make up the derived event. Should be exactly one in case of instance event type. Should be atleast two in case of sequence or merge event type.'
    ),
  uniquenessFilters: z
    .array(UniquenessFilterSchema)
    .max(1)
    .describe('Uniqueness filters for the derived event, only to be used for merged events'),
});

const IndexDateSchema = z.object({
  type: IndexDateTypeSchema,
  indexDateValue: z
    .string()
    .nullish()
    .describe(
      'Event instance when event type is EVENT_INSTANCE, date type when event type is CALENDAR_DATE, in YYYY-MM-DD format'
    ),
  indexDateRangeValue: IndexDateRangeSchema.optional()
    .nullable()
    .describe(
      'Optional date range restriction when event type is EVENT_INSTANCE, in YYYY-MM-DD format'
    ),
});

const SubjectVariableSchema = z.object({
  id: z.string().uuid().describe('ID of the subject variable'),
  name: z
    .string()
    .min(1)
    .max(NAME_MAX_CHARACTER_LENGTH)
    .regex(NAME_VALID_CHARACTERS_WITHOUT_DOUBLE_QUOTE)
    .describe('Name of the I/E variable'),
  type: SubjectVariableTypeSchema.describe(
    "Type of the I/E variable's criteria, either COUNT or DEMOGRAPHICS"
  ),
  conceptFilters: z
    .array(DemographicsConceptFilterSchema)
    .describe('Concept filters for the I/E variable, to be used when type is DEMOGRAPHICS'),
  numericFilters: z
    .array(DemographicsNumericFilterSchema)
    .describe('Numeric filters for the I/E variable, to be used when type is DEMOGRAPHICS'),
  countFilter: CountFilterSchema.optional()
    .nullable()
    .describe('Count filter for the subject variable, to be used when type is COUNT'),
  criteriaSelection: CriteriaSelectionSchema.describe(
    'Criteria selection for the subject variable'
  ),
  timeWindow: TimeWindowFilterSchema.optional()
    .nullable()
    .describe(
      'Time duration window inclusion/exclusion variable criteria is applied to. Applicable only when type is COUNT'
    ),
});

/**
 * Subject variable usage as I/E criteria in a cohort definition.
 * References a SubjectVariable by ID and acts as a criterion.
 */
const SubjectVariableCriterionSchema = z.object({
  /**
   * Discriminator field for criteria union.
   */
  criteriaType: z.literal('subject_variable'),
  /**
   * ID of the subject variable reference.
   */
  subjectVariableId: z.string().uuid().describe('ID of the subject variable reference'),
});

/**
 * Calendar date index date criterion value.
 */
const IndexDateCriterionCalendarDateSchema = z.object({
  type: z.literal('Calendar date'),
  indexDateValue: z.string().describe('Calendar date value in YYYY-MM-DD format'),
});

/**
 * How to resolve the index date from qualifying events.
 */
const IndexDateResolutionSchema = z.object({
  occurrence: z.enum(['first', 'last', 'nth']),
  nthValue: z.number().int().positive().optional().nullable(),
  // 'all': the index is the occurrence at which every other criterion is also met.
  // 'none': the absolute occurrence. Null/absent: no rule recorded, read as 'none'.
  dependentCriteriaMode: z.enum(['all', 'none']).optional().nullable(),
});

/**
 * Analytical variable index date criterion value.
 *
 * `analyticalVariableId` is nullable to match the backend DSL (PR #3647).
 * For library-reference index dates, set `sourceLibraryAvId` and leave
 * `analyticalVariableId` null; the runner resolves the AV from the library
 * at execution time.
 */
const IndexDateCriterionAnalyticalVariableSchema = z.object({
  type: z.literal('Analytical variable'),
  analyticalVariableId: z
    .string()
    .uuid()
    .nullable()
    .describe(
      'ID of the analytical variable in the cohort-local `analyticalVariables` array, or null when this index date references a library AV via `sourceLibraryAvId`. The referenced AV must have BOOLEAN output with ANY aggregation.'
    ),
  dateResolution: IndexDateResolutionSchema.describe(
    'How to resolve the index date from qualifying events.'
  ),
  // Library provenance: set when index date AV is published to or added from the AV library.
  // Required when `analyticalVariableId` is null (library-reference mode).
  sourceLibraryAvId: z.string().uuid().nullable().optional(),
  // Plain-language description for unbound/stub index criteria emitted by
  // the cohort_drafter — surfaced in the UI before an AV is bound.
  description: z.string().nullable().optional(),
  // Backend-owned user-visible title (OR-6077). On a stub it is the
  // description; the UI prefers the bound AV's name once one is attached.
  // Null only on pre-migration cohort versions.
  name: z.string().nullable().optional(),
});

/**
 * Discriminated union for index date criterion values.
 */
const IndexDateCriterionValueSchema = z.discriminatedUnion('type', [
  IndexDateCriterionCalendarDateSchema,
  IndexDateCriterionAnalyticalVariableSchema,
]);

/**
 * Index date criterion for use in the cohort criteria array.
 * Uses discriminated unions to support calendar date and analytical variable index dates.
 */
const IndexDateCriterionSchema = z.object({
  /**
   * Discriminator field for criteria union.
   */
  criteriaType: z.literal('index_date'),
  /**
   * Index date criterion value.
   */
  value: IndexDateCriterionValueSchema,
  /**
   * Change tracking status (NEW, EDITED) set by the Research Assistant.
   */
  changeStatus: z.string().optional().nullable(),
  /**
   * Who made the change (RESEARCH_ASSISTANT).
   */
  changedBy: z.string().optional().nullable(),
});

/**
 * Discriminated union for criteria (subject variables, analytical variables, and index date).
 * When generating cohort definitions, prioritize using AnalyticalVariableCriterion first.
 * Only use SubjectVariableCriterion if the logic cannot be expressed as an AnalyticalVariableCriterion.
 * IndexDateCriterion provides a new way to specify index dates using discriminated unions.
 */
const CriterionSchema = z.discriminatedUnion('criteriaType', [
  SubjectVariableCriterionSchema,
  AnalyticalVariableCriterionSchema,
  IndexDateCriterionSchema,
]);

const StudyPeriodRestrictionsSchema = z.object({
  overallStudyPeriodRestriction: OverallStudyPeriodRestrictionSchema.optional()
    .nullable()
    .describe(
      'Restriction on the overall study period for the cohort. This will be applied to all events. Dates are in YYYY-MM-DD format and are inclusive.'
    ),
  baselinePeriodRestriction: BaselinePeriodRestrictionSchema.optional()
    .nullable()
    .describe('Restriction on the baseline period for the cohort.'),
  followUpPeriodRestriction: FollowUpPeriodRestrictionSchema.optional()
    .nullable()
    .describe('Restriction on the follow-up period for the cohort.'),
});

// Main CohortDefinition schema
export const CohortDefinitionZodModel = z
  .object({
    simpleEvents: z
      .array(SimpleEventSchema)
      .describe(
        'Simple events are the basic building blocks of the cohort definition, mapping to OMOP tables'
      ),
    derivedEvents: z
      .array(DerivedEventSchema)
      .describe(
        'Derived events reference simple events or derived events to create more complex event filters'
      ),
    indexDate: IndexDateSchema.optional()
      .nullable()
      .describe('Index date for the cohort, either event or calendar date'),
    subjectVariables: z
      .array(SubjectVariableSchema)
      .describe('I/E variables for the cohort, including count and demographics'),
    /**
     * Analytical variables for use in the cohort I/E criteria.
     *
     * FEATURE FLAG: This field is optional due to `analytical_variables_in_cohort_builder` feature flag.
     * Runtime validation in application code checks the feature flag before requiring this field.
     *
     * CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag:
     * - Remove `.optional()` and make this field required
     * - Remove feature flag checks from runtime validation code
     * - Update any conditional logic that gates on this field's presence
     */
    analyticalVariables: z
      .array(AnalyticalVariableSchema)
      .optional()
      .describe('Analytical variables for use in the cohort I/E criteria'),
    /**
     * I/E criteria for the cohort, including subject variables, analytical variables, and index date.
     * PRIORITY: Always try to express criteria as AnalyticalVariableCriterion first.
     * Only use SubjectVariableCriterion if the logic cannot be expressed as an AnalyticalVariableCriterion.
     * Analytical variables provide more flexible and powerful logic for complex criteria.
     * IndexDateCriterion provides a new way to specify index dates using discriminated unions.
     *
     * FEATURE FLAG: This field is optional due to `analytical_variables_in_cohort_builder` feature flag.
     * Runtime validation in application code checks the feature flag before requiring this field.
     *
     * CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag:
     * - Remove `.optional()` and make this field required
     * - Remove feature flag checks from runtime validation code
     * - Update any conditional logic that gates on this field's presence
     */
    criteria: z
      .array(CriterionSchema)
      .optional()
      .describe(
        'I/E criteria for the cohort, including subject variables and analytical variables. PRIORITY: Always try to express criteria as AnalyticalVariableCriterion first. Only use SubjectVariableCriterion if the logic cannot be expressed as an AnalyticalVariableCriterion. Analytical variables provide more flexible and powerful logic for complex criteria.'
      ),
    studyPeriodRestrictions: StudyPeriodRestrictionsSchema.optional()
      .nullable()
      .describe('Constraints on the study period and observation periods for the cohort'),
  })
  .strict()
  .superRefine((data, ctx) => {
    // Validate that AVs referenced by IndexDateCriterion do not self-reference INDEX_DATE as anchor
    const indexDateCriteria = (data.criteria ?? []).filter((c) => c.criteriaType === 'index_date');
    for (const criterion of indexDateCriteria) {
      if (criterion.value.type === 'Analytical variable') {
        const avId = criterion.value.analyticalVariableId;
        const av = (data.analyticalVariables ?? []).find((a) => a.id === avId);
        if (
          av &&
          (av.window.start.anchor === 'index_date' || av.window.end.anchor === 'index_date')
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `IndexDateCriterion references analytical variable ${avId} whose time window anchor is INDEX_DATE. Index date variables cannot self-reference the index date.`,
            path: ['criteria'],
          });
        }
      }
    }
  });

export type CohortDefinitionSchema = z.infer<typeof CohortDefinitionZodModel>;

// Export IndexDateCriterion types
export type IndexDateResolution = z.infer<typeof IndexDateResolutionSchema>;
export type IndexDateCriterionCalendarDate = z.infer<typeof IndexDateCriterionCalendarDateSchema>;
export type IndexDateCriterionAnalyticalVariable = z.infer<
  typeof IndexDateCriterionAnalyticalVariableSchema
>;
export type IndexDateCriterionValue = z.infer<typeof IndexDateCriterionValueSchema>;
export type IndexDateCriterion = z.infer<typeof IndexDateCriterionSchema>;
